import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  Paper,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const ThemePreview = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const ColorSwatch = ({ label, color, textColor = '#fff' }) => (
    <Box sx={{ textAlign: 'center' }}>
      <Paper
        elevation={2}
        sx={{
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: color,
          color: textColor,
          fontWeight: 600,
          fontSize: '0.75rem',
          mb: 1,
        }}
      >
        {color}
      </Paper>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Current Theme Preview - {isDark ? 'Dark Mode' : 'Light Mode'}
      </Typography>

      <Grid container spacing={3}>
        {/* Color Palette */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Color Palette
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Primary"
                    color={theme.palette.primary.main}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Primary Light"
                    color={theme.palette.primary.light}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Primary Dark"
                    color={theme.palette.primary.dark}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Secondary"
                    color={theme.palette.secondary.main}
                    textColor={isDark ? '#000' : '#fff'}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Background"
                    color={theme.palette.background.default}
                    textColor={theme.palette.text.primary}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <ColorSwatch
                    label="Paper"
                    color={theme.palette.background.paper}
                    textColor={theme.palette.text.primary}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Colors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Status Colors
              </Typography>
              <Stack spacing={2}>
                <Chip
                  icon={<CheckIcon />}
                  label="Success - Operation completed"
                  color="success"
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  icon={<InfoIcon />}
                  label="Info - Additional information"
                  color="info"
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  icon={<WarningIcon />}
                  label="Warning - Attention required"
                  color="warning"
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  icon={<ErrorIcon />}
                  label="Error - Action failed"
                  color="error"
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Buttons */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Button Styles
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" color="primary">
                    Primary
                  </Button>
                  <Button variant="contained" color="secondary">
                    Secondary
                  </Button>
                  <Button variant="contained" disabled>
                    Disabled
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" color="primary">
                    Outlined
                  </Button>
                  <Button variant="outlined" color="secondary">
                    Secondary
                  </Button>
                  <Button variant="outlined" disabled>
                    Disabled
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="text" color="primary">
                    Text
                  </Button>
                  <Button variant="text" color="secondary">
                    Secondary
                  </Button>
                  <Button variant="text" disabled>
                    Disabled
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Typography */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Typography
              </Typography>
              <Stack spacing={1}>
                <Typography variant="h4">Heading 4 - Playfair Display</Typography>
                <Typography variant="h6">Heading 6 - Playfair Display</Typography>
                <Typography variant="body1">
                  Body 1 - Regular text with good readability in both light and dark modes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Body 2 - Secondary text for supporting information
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Caption - Small supporting text for labels and metadata
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Theme Info */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Theme Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Mode
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {isDark ? 'Dark' : 'Light'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Primary Color
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {theme.palette.primary.main}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Background
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {theme.palette.background.default}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Border Radius
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {theme.shape.borderRadius}px
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ThemePreview;
